import React from "react";
import extend from "lodash/extend";
import pick from "lodash/pickBy";
import identity from "lodash/identity";
import Helmet from "react-helmet";

import * as util from "../util";
import EventList from "./partials/event-list/event-list.jsx";
import InjectedTenantHTML from "./partials/injected-tenant-html.jsx";
import ModeSwitcher from "./partials/mode-switcher.jsx";
import FilmMarquee from "./partials/film-marquee.jsx";

import connectToStores from "alt-utils/lib/connectToStores";
import EventStore from "../stores/EventStore";
import EventActions from "../actions/EventActions";
import FilmStore from "../stores/FilmStore";
import WishlistStore from "../stores/WishlistStore";
import PassStore from "../stores/PassStore";
import TicketStore from "../stores/TicketStore";
import PersonStore from "../stores/PersonStore";

@connectToStores
class Schedule extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      query: {},
      hasFetchedPastInitially: false
    };

    this._getDefaultViewMode = this._getDefaultViewMode.bind(this);
    this._onStateChange = this._onStateChange.bind(this);
    this.getStyles = this.getStyles.bind(this);
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object.isRequired,
      tenant: React.PropTypes.object,
      isLarge: React.PropTypes.bool,
      isEmbed: React.PropTypes.bool
    };
  }

  static getStores() {
    return [
      EventStore,
      FilmStore,
      WishlistStore,
      PassStore,
      TicketStore,
      PersonStore
    ];
  }

  static getPropsFromStores() {
    let store = EventStore.getState();
    store.films = FilmStore.getState().films;
    store.wishlist = WishlistStore.getState().wishlist;
    return store;
  }

  componentWillMount() {
    const hasPastViewMode =
      ["grid", "timeline"].indexOf(
        this.props.location.query.viewMode || this._getDefaultViewMode()
      ) !== -1;
    if (hasPastViewMode) {
      this.setState({ hasFetchedPastInitially: true });
    }

    EventActions.fetchAll.defer({
      includePastEvents: hasPastViewMode,
      includeVirtualEvents: this.props.appSettings.schedule_include_virtual
        ? true
        : false
    });
    this.componentWillReceiveProps(this.props);
  }

  _getDefaultViewMode() {
    if (!this.context.isLarge) {
      return this.context.tenant.schedule_default_mobile_view_mode;
    } else {
      return this.context.tenant.schedule_default_view_mode;
    }
  }

  _onStateChange(newState, stateOptions) {
    let query = extend({}, this.props.location.query, newState),
      viewMode = query.viewMode || this._getDefaultViewMode();

    if (viewMode !== "grid") {
      delete query.date;
    }
    if (viewMode !== "timeline") {
      delete query.range;
    }

    let nonEmptyQuery = pick(query, identity); // omit empty fields

    // Don't mess up an in-progress dialog - we'll have to catch up later, somehow
    if (this.props.location.pathname.indexOf("/schedule/quick") !== -1) {
      return;
    }

    // Don't block render loop
    setTimeout(() => {
      this.context.router.push({
        pathname: "/schedule",
        query: nonEmptyQuery,
        state: extend({ ignoreScrollBehavior: true }, stateOptions)
      });
    }, 0);
  }

  componentWillReceiveProps(newProps) {
    // Only update the filter query when it is updated on the root /schedule page. This keeps us from losing the filter data
    // when a user browses to a schedule/quick page (for quick ordering passholder tickets), which doesn't have any query string
    // but should nonetheless preserve whatever was previously entered.
    // (Note: Strip the slashes to ensure we match both /schedule and /schedule/.)
    if (newProps.location.pathname.replace(/\//g, "") === "schedule") {
      this.setState({
        query: newProps.location.query
      });
    }
  }

  getStyles() {
    return {
      injectStyle: {
        marginTop: 10,
        fontSize: 16
      },
      smallInjectStyle: {
        paddingLeft: 10,
        paddingRight: 10
      }
    };
  }

  render() {
    var styles = this.getStyles(),
      injectStyle = Object.assign(
        {},
        styles.injectStyle,
        !this.context.isLarge && styles.smallInjectStyle
      ),
      { query, hasFetchedPastInitially } = this.state;


    if (!this.props.previewMode && !this.props.appSettings.schedule_enabled) {
      return (
        <div>
          <ModeSwitcher value="schedule" />
          <div>
            {this.context.tenant.schedule_unreleased_custom.__html ? (
              <InjectedTenantHTML
                style={injectStyle}
                content={this.context.tenant.schedule_unreleased_custom}
              />
            ) : (
              <div
                style={Object.assign({}, injectStyle, { textAlign: "center" })}
              >
                The schedule has not yet been released. Check back soon!
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="Eventive--SchedulePage">
        <Helmet title={util.capitalize(this.context.tenant.display_schedule)} />
        {this.context.tenant.schedule_show_film_marquee ? (<FilmMarquee/>) : null}
        {this.props.children
          ? React.cloneElement(this.props.children, {
              appSettings: this.props.appSettings
            })
          : undefined}{" "}
        {/* For quick order overlay */}
        <ModeSwitcher value="schedule" />
        {(!this.context.isEmbed ||
          this.props.location.query.showCustomContent) &&
        this.context.tenant.schedule_custom.__html ? ( // Custom HTML is hidden when embedded
          <InjectedTenantHTML
            style={injectStyle}
            content={this.context.tenant.schedule_custom}
          />
        ) : null}
        <EventList
          appSettings={this.props.appSettings}
          events={this.props.events}
          showLoadingIndicator={
            !this.props.hasFetchedAll || PassStore.isFetchingFirstTime()
          }
          search={query.search || ""}
          viewMode={query.viewMode || this._getDefaultViewMode()}
          filterTags={query.filterTags || {}}
          filterVenues={query.filterVenues || {}}
          justMyEvents={query.justMyEvents}
          timelineRange={query.range}
          gridDate={query.date}
          onStateChange={this._onStateChange}
          offerFetchPast={!this.props.hasFetchedAllWithPast}
          fetchingPast={this.props.isFetchingAllPast}
          fetchPast={
            !hasFetchedPastInitially
              ? () => EventActions.fetchAll.defer({ includePastEvents: true })
              : undefined
          }
          groupItemsBy={this.context.tenant.schedule_group_events_by}
          hasWishlist={
            this.props.wishlist && this.props.wishlist.events.length > 0
          }
          passes={PassStore.getPassesForCurrentEventBucket()}
        />
      </div>
    );
  }
}

export default Schedule;



// WEBPACK FOOTER //
// ./src/app/components/schedule.jsx